import constants from '../../../helpers/constants';
import moduleGanttDateWorker from '../../gantt/modules/ganttDateWorker';
import moment from '../../../libs/moment';
import projectsModel from '../../../models/projects';
import Store from '../../../store/main';
import charts from './chartsConfig';

const MONTH_LOCALES = [
  'report_month_jan',
  'report_month_feb',
  'report_month_march',
  'report_month_apr',
  'report_month_may',
  'report_month_jun',
  'report_month_jul',
  'report_month_aug',
  'report_month_sep',
  'report_month_oct',
  'report_month_nov',
  'report_month_dec',
];

class Progress {
  async getPlannedProgress(projectId, tasks, lastDayOfMonth) {
    const projectConfig = projectsModel.getProjectConfig(projectId);
    const isProgressTypeDuration = projectConfig.progress === 'duration';

    const _tasksDuration = tasks.reduce((sum, task) => sum + task.duration, 0) || 1;
    const todayTasks = tasks.filter(task => !moment(task.end_date).isBefore() && !moment(task.start_date).isAfter());

    const getLastDayTasks = date => tasks.filter(task => !moment(task.end_date).isBefore(date) && !moment(task.start_date).isAfter(date));

    const lastTasks = lastDayOfMonth ? getLastDayTasks(lastDayOfMonth) : todayTasks;
    const preparedLastDayTasks = lastTasks.map(task => ({
      id: task.id,
      start_date: new Date(task.start_date),
      end_date: lastDayOfMonth ? new Date(lastDayOfMonth) : new Date(),
    }));

    const durationData = await moduleGanttDateWorker.calculateDuration(projectId, preparedLastDayTasks);

    const tasksCount = tasks.length || 1;
    const _duration = tasks.reduce((sum, task) => {
      let progress;

      const isBefore = lastDayOfMonth ? moment(task.end_date).isBefore(lastDayOfMonth) : moment(task.end_date).isBefore();
      const isAfter = lastDayOfMonth ? moment(task.start_date).isAfter(lastDayOfMonth) : moment(task.start_date).isAfter();

      if (isBefore) {
        progress = 1;
      } else if (isAfter) {
        progress = 0;
      } else {
        const duration = durationData[task.id] || 0;

        progress = +(duration / task.duration).toFixed(2);
      }

      if (isProgressTypeDuration) {
        return sum + progress * task.duration;
      }

      return sum + progress;
    }, 0);

    const _plannedProgress = isProgressTypeDuration
      ? _duration / _tasksDuration
      : _duration / tasksCount;
    const plannedProgressResultValue = Math.round(_plannedProgress * 100);

    return plannedProgressResultValue;
  }

  async getPlannedToBaselineValues(projectId, tasks, baselineTasks, lastDayOfMonth) {
    const projectConfig = projectsModel.getProjectConfig(projectId);
    const isProgressTypeDuration = projectConfig.progress === 'duration';

    const _baselineTasksDuration = baselineTasks.reduce((sum, task) => sum + task.duration, 0) || 1;
    const baselineTasksByMonth = baselineTasks.filter(task => moment(task.end_date).isBefore(lastDayOfMonth));
    const baselineTasksByMonthCount = baselineTasksByMonth.length || 1;

    const plannedProgressResultValue = await this.getPlannedProgress(projectId, tasks, lastDayOfMonth);

    const _baselineTasksProgress = baselineTasksByMonth.reduce((sum, task) => {
      if (isProgressTypeDuration) {
        return sum + task.progress * task.duration;
      }

      return sum + task.progress;
    }, 0);

    const _baselineProgress = isProgressTypeDuration
      ? _baselineTasksProgress / _baselineTasksDuration
      : _baselineTasksProgress / baselineTasksByMonthCount;

    const baselineProgressResultValue = Math.round(_baselineProgress * 100);

    return {
      baselineProgress: baselineProgressResultValue,
      plannedProgress: plannedProgressResultValue,
    };
  }

  getActualToBaselineValues(projectId, tasks, baselineTasks, lastDayOfMonth) {
    const projectConfig = projectsModel.getProjectConfig(projectId);
    const isProgressTypeDuration = projectConfig.progress === 'duration';

    const _tasksDuration = tasks.reduce((sum, task) => sum + task.duration, 0) || 1;
    const _baselineTasksDuration = baselineTasks.reduce((sum, task) => sum + task.duration, 0) || 1;

    const tasksByMonth = tasks.filter(task => moment(task.start_date).isBefore(lastDayOfMonth));
    const tasksByMonthCount = tasksByMonth.length || 1;
    const baselineTasksByMonth = baselineTasks.filter(task => moment(task.start_date).isBefore(lastDayOfMonth));
    const baselineTasksByMonthCount = baselineTasksByMonth.length || 1;

    const _tasksProgress = tasksByMonth.reduce((sum, task) => {
      if (isProgressTypeDuration) {
        return sum + task.progress * task.duration;
      }

      return sum + task.progress;
    }, 0);

    const _actualProgress = isProgressTypeDuration
      ? _tasksProgress / _tasksDuration
      : _tasksProgress / tasksByMonthCount;

    const actualProgressResultValue = Math.round(_actualProgress * 100);

    const _baselineTasksProgress = baselineTasksByMonth.reduce((sum, task) => {
      if (isProgressTypeDuration) {
        return sum + task.progress * task.duration;
      }

      return sum + task.progress;
    }, 0);

    const _baselineProgress = isProgressTypeDuration
      ? _baselineTasksProgress / _baselineTasksDuration
      : _baselineTasksProgress / baselineTasksByMonthCount;

    const baselineProgressResultValue = Math.round(_baselineProgress * 100);

    return {
      actualProgress: actualProgressResultValue,
      baselineProgress: baselineProgressResultValue,
    };
  }

  async getActualToPlannedValues(projectId, tasks, lastDayOfMonth) {
    const projectConfig = projectsModel.getProjectConfig(projectId);
    const isProgressTypeDuration = projectConfig.progress === 'duration';

    const _tasksDuration = tasks.reduce((sum, task) => sum + task.duration, 0) || 1;
    const tasksByMonth = tasks.filter(task => moment(task.start_date).isBefore(lastDayOfMonth));
    const tasksByMonthCount = tasksByMonth.length || 1;

    const _tasksProgress = tasksByMonth.reduce((sum, task) => {
      if (isProgressTypeDuration) {
        return sum + task.progress * task.duration;
      }

      return sum + task.progress;
    }, 0);

    const _actualProgress = isProgressTypeDuration
      ? _tasksProgress / _tasksDuration
      : _tasksProgress / tasksByMonthCount;

    const actualProgressResultValue = Math.round(_actualProgress * 100);
    const plannedProgressResultValue = await this.getPlannedProgress(projectId, tasks, lastDayOfMonth);

    return {
      actualProgress: actualProgressResultValue,
      plannedProgress: plannedProgressResultValue,
    };
  }

  async calculateMonthProgressReport(data) {
    const {
      projectId, calculationMode, baselineTasks,
    } = data;

    if (!projectId) return null;

    const chartValues_1 = [];
    const chartValues_2 = [];
    const xAxisValues = [];

    const currentFilter = Store.getters['filter/currentFilter'];
    const projectTasks = Store.getters['tasksModel/getTasksForGantt'](projectId).data;
    const tasks = projectTasks.filter(task => task.type === constants.TASK_TYPES.task || task.type === constants.TASK_TYPES.milestone);
    const totalEstimate = projectTasks.find(task => task.type === constants.TASK_TYPES.project && !task.parent);

    const startDate = currentFilter?.properties?.rangeDate?.start ? moment(currentFilter?.properties?.rangeDate?.start) : moment(totalEstimate?.start_date);
    const endDate = currentFilter?.properties?.rangeDate?.end ? moment(currentFilter?.properties?.rangeDate?.end) : moment(totalEstimate?.end_date);
    const startMonth = startDate.clone().startOf('month');
    const endMonth = endDate.clone().startOf('month');
    const monthsDifference = endMonth.diff(startMonth, 'months');

    for (let i = 0; i <= monthsDifference; i++) {
      const currentMonth = moment(startDate).add(i, 'months');
      const lastDayOfMonth = currentMonth.endOf('month');
      const dateString = `${MONTH_LOCALES[currentMonth.month()]} ${currentMonth.year()}`;

      xAxisValues.push(dateString);

      if (calculationMode === constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED) {
        const progressValues = await this.getActualToPlannedValues(projectId, tasks, lastDayOfMonth);

        chartValues_1.push(progressValues.plannedProgress);
        chartValues_2.push(progressValues.actualProgress);
      }

      if (calculationMode === constants.OVERVIEW_TYPES.ACTUAL_TO_BASELINE) {
        const progressValues = this.getActualToBaselineValues(projectId, tasks, baselineTasks, lastDayOfMonth);

        chartValues_1.push(progressValues.baselineProgress);
        chartValues_2.push(progressValues.actualProgress);
      }

      if (calculationMode === constants.OVERVIEW_TYPES.PLANNED_TO_BASELINE) {
        const progressValues = await this.getPlannedToBaselineValues(projectId, tasks, baselineTasks, lastDayOfMonth);

        chartValues_1.push(progressValues.plannedProgress);
        chartValues_2.push(progressValues.baselineProgress);
      }
    }

    if (!chartValues_1.length && !chartValues_2.length) {
      return null;
    }

    const defaultConfig = charts.monthProgressChart(calculationMode).default;
    const resultConfig = {
      ...defaultConfig,
      series: [
        { ...defaultConfig.series[0], data: chartValues_1 },
        { ...defaultConfig.series[1], data: chartValues_2 },
      ],
      xAxis: { ...defaultConfig.xAxis, data: xAxisValues },
    };

    return resultConfig;
  }

  // async calculateScheduleReport(data) {
  //   const {
  //     projectIds, calculationMode, baselineData,
  //   } = data;
  //   const resultConfig = _.cloneDeep(charts.scheduleProgressChart.default);

  //   let yValues = [];
  //   let xValues = [];

  //   for (let i = 0; i < projectIds.length; i++) {
  //     let allTasks;

  //     if (calculationMode === constants.OVERVIEW_TYPES.ACTUAL_TO_BASELINE) {
  //       allTasks = baselineData?.tasks || [];
  //     } else {
  //       allTasks = Store.getters['tasksModel/getTasksForGantt'](projectIds[i]).data;
  //     }

  //     const tasksAndMilestones = allTasks.filter(task => task.type === constants.TASK_TYPES.task || task.type === constants.TASK_TYPES.milestone);
  //     const totalEstimate = allTasks.find(task => task.type === constants.TASK_TYPES.project && !task.parent);
  //     const projectData = projectsModel.getProjectDataById(projectIds[i]);

  //     const plannedProgress = await this.getPlannedProgress(projectIds[i], tasksAndMilestones);
  //     const actualProgress = totalEstimate ? Math.round(totalEstimate.progress * 100) : 0;
  //     const variance = actualProgress - plannedProgress;

  //     if (calculationMode === constants.OVERVIEW_TYPES.PLANNED_TO_BASELINE) {
  //       const baselineTasks = baselineData?.tasks || [];
  //       const baselineTasksAndMilestones = baselineTasks.filter(task => task.type === constants.TASK_TYPES.task || task.type === constants.TASK_TYPES.milestone);

  //       const baselineProgress = await this.getPlannedProgress(projectIds[i], baselineTasksAndMilestones);
  //       const plannedVariance = plannedProgress - baselineProgress;

  //       yValues = [
  //         (plannedVariance > 0 ? __('overview_ahead') : __('overview_behind')),
  //         __('overview_planned_progress'),
  //         __('overview_baseline_planned_progress'),
  //       ];
  //       xValues = [
  //         {
  //           value: plannedVariance === 0 ? 0.01 : plannedVariance,
  //           realValue: plannedVariance,
  //           label: {
  //             position: plannedVariance === 0 ? 'right' : 'inside',
  //             color: plannedVariance === 0 ? '#191919' : '#fff',
  //           },
  //           progressData: {
  //             isSingleProjectReport: true,
  //             projectName: projectData.name,
  //             name: (plannedVariance > 0 ? __('overview_ahead') : __('overview_behind')),
  //             isVariance: true,
  //           },
  //         },
  //         {
  //           value: plannedProgress === 0 ? 0.01 : plannedProgress,
  //           realValue: plannedProgress,
  //           label: {
  //             position: plannedProgress === 0 ? 'right' : 'inside',
  //             color: plannedProgress === 0 ? '#191919' : '#fff',
  //           },
  //           progressData: {
  //             isSingleProjectReport: true,
  //             projectName: projectData.name,
  //             name: __('overview_planned_progress'),
  //             isActual: true,
  //           },
  //         },
  //         {
  //           value: baselineProgress === 0 ? 0.01 : baselineProgress,
  //           realValue: baselineProgress,
  //           label: {
  //             position: baselineProgress === 0 ? 'right' : 'inside',
  //             color: baselineProgress === 0 ? '#191919' : '#fff',
  //           },
  //           progressData: {
  //             isSingleProjectReport: true,
  //             projectName: projectData.name,
  //             name: __('overview_baseline_planned_progress'),
  //             isPlanned: true,
  //           },
  //         },
  //       ];

  //       resultConfig.series[0].itemStyle.color = params => {
  //         if (params.data.progressData.isPlanned) {
  //           return '#458C9A';
  //         }
  //         if (params.data.progressData.isActual) {
  //           return '#1565C0';
  //         }

  //         if (params.data.progressData.isVariance) {
  //           return (params.value > 0 ? '#8BC34A' : '#EE7950');
  //         }
  //       };
  //       resultConfig.series[0].data = xValues;
  //       resultConfig.yAxis.data = yValues;

  //       return { config: resultConfig };
  //     }

  //     if (projectIds.length === 1) {
  //       yValues = [
  //         (variance > 0 ? __('overview_ahead') : __('overview_behind')),
  //         __('overview_actual_progress'),
  //         __('overview_planned_progress'),
  //       ];
  //       xValues = [
  //         {
  //           value: variance === 0 ? 0.01 : variance,
  //           realValue: variance,
  //           label: {
  //             position: variance === 0 ? 'right' : 'inside',
  //             color: variance === 0 ? '#191919' : '#fff',
  //           },
  //           itemStyle: {
  //             color: variance < 0 ? '#EE7950' : '#8BC34A',
  //             borderRadius: variance < 0 ? [4, 0, 0, 4] : [0, 4, 4, 0],
  //           },
  //           progressData: {
  //             isSingleProjectReport: true,
  //             projectName: projectData.name,
  //             name: (variance > 0 ? __('overview_ahead') : __('overview_behind')),
  //             isVariance: true,
  //           },
  //         },
  //         {
  //           value: actualProgress === 0 ? 0.01 : actualProgress,
  //           realValue: actualProgress,
  //           label: {
  //             position: actualProgress === 0 ? 'right' : 'inside',
  //             color: actualProgress === 0 ? '#191919' : '#fff',
  //           },
  //           itemStyle: {
  //             color: '#1565C0',
  //             borderRadius: actualProgress < 0 ? [4, 0, 0, 4] : [0, 4, 4, 0],
  //           },
  //           progressData: {
  //             isSingleProjectReport: true,
  //             projectName: projectData.name,
  //             name: __('overview_actual_progress'),
  //             isActual: true,
  //           },
  //         },
  //         {
  //           value: plannedProgress === 0 ? 0.01 : plannedProgress,
  //           realValue: plannedProgress,
  //           label: {
  //             position: plannedProgress === 0 ? 'right' : 'inside',
  //             color: plannedProgress === 0 ? '#191919' : '#fff',
  //           },
  //           itemStyle: {
  //             color: '#458C9A',
  //             borderRadius: plannedProgress < 0 ? [4, 0, 0, 4] : [0, 4, 4, 0],
  //           },
  //           progressData: {
  //             isSingleProjectReport: true,
  //             projectName: projectData.name,
  //             name: __('overview_planned_progress'),
  //             isPlanned: true,
  //           },
  //         },
  //       ];

  //       resultConfig.series[0].data = xValues;
  //       resultConfig.yAxis.data = yValues;

  //       return { config: resultConfig };
  //     }

  //     if (variance !== 0) {
  //       yValues.push(projectData.name);
  //       xValues.push({
  //         value: variance,
  //         progressData: {
  //           isSingleProjectReport: false,
  //           projectName: projectData.name,
  //           progressValue_1: actualProgress,
  //           progressValue_2: plannedProgress,
  //         },
  //       });
  //     }
  //   }

  //   if (xValues.length) {
  //     resultConfig.series[0].data = xValues.map(item => ({
  //       ...item,
  //       itemStyle: {
  //         color: item.value < 0 ? '#EE7950' : '#8BC34A',
  //         borderRadius: item.value < 0 ? [4, 0, 0, 4] : [0, 4, 4, 0],
  //       },
  //       label: {
  //         position: item.value < 0 ? 'inside' : 'insideRight',
  //       },
  //     }));
  //     resultConfig.yAxis.data = yValues;

  //     return { config: resultConfig };
  //   }

  //   return null;
  // }

  async calculateScheduleReport(data) {
    const {
      projectIds, calculationMode, baselineData,
    } = data;
    const resultConfig = _.cloneDeep(charts.scheduleProgressChart.default);

    let yValues = [];
    let xValues = [];

    for (let i = 0; i < projectIds.length; i++) {
      const projectData = projectsModel.getProjectDataById(projectIds[i]);

      const allTasks = Store.getters['tasksModel/getTasksForGantt'](projectIds[i]).data;
      const tasksAndMilestones = allTasks.filter(task => task.type === constants.TASK_TYPES.task || task.type === constants.TASK_TYPES.milestone);
      const totalEstimate = allTasks.find(task => task.type === constants.TASK_TYPES.project && !task.parent);
      const actualProgress = totalEstimate ? Math.round(totalEstimate.progress * 100) : 0;
      const plannedProgress = await this.getPlannedProgress(projectIds[i], tasksAndMilestones);

      let variance = 0;

      if (calculationMode === constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED) {
        variance = actualProgress - plannedProgress;

        if (projectIds.length === 1) {
          yValues = [
            (variance > 0 ? __('overview_ahead') : __('overview_behind')),
            __('overview_actual_progress'),
            __('overview_planned_progress'),
          ];
          xValues = [
            {
              value: variance === 0 ? 0.01 : variance,
              realValue: variance,
              label: {
                position: variance === 0 ? 'right' : 'inside',
                color: variance === 0 ? '#191919' : '#fff',
              },
              itemStyle: {
                color: variance < 0 ? '#EE7950' : '#8BC34A',
                borderRadius: variance < 0 ? [4, 0, 0, 4] : [0, 4, 4, 0],
              },
              progressData: {
                isSingleProjectReport: true,
                projectName: projectData.name,
                name: (variance > 0 ? __('overview_ahead') : __('overview_behind')),
                isVariance: true,
              },
            },
            {
              value: actualProgress === 0 ? 0.01 : actualProgress,
              realValue: actualProgress,
              label: {
                position: actualProgress === 0 ? 'right' : 'inside',
                color: actualProgress === 0 ? '#191919' : '#fff',
              },
              itemStyle: {
                color: '#1565C0',
                borderRadius: actualProgress < 0 ? [4, 0, 0, 4] : [0, 4, 4, 0],
              },
              progressData: {
                isSingleProjectReport: true,
                projectName: projectData.name,
                name: __('overview_actual_progress'),
                isActual: true,
              },
            },
            {
              value: plannedProgress === 0 ? 0.01 : plannedProgress,
              realValue: plannedProgress,
              label: {
                position: plannedProgress === 0 ? 'right' : 'inside',
                color: plannedProgress === 0 ? '#191919' : '#fff',
              },
              itemStyle: {
                color: '#458C9A',
                borderRadius: plannedProgress < 0 ? [4, 0, 0, 4] : [0, 4, 4, 0],
              },
              progressData: {
                isSingleProjectReport: true,
                projectName: projectData.name,
                name: __('overview_planned_progress'),
                isPlanned: true,
              },
            },
          ];

          resultConfig.series[0].data = xValues;
          resultConfig.yAxis.axisLabel.fontSize = 16;
          resultConfig.yAxis.data = yValues;

          return { config: resultConfig };
        }
      }

      if (calculationMode === constants.OVERVIEW_TYPES.ACTUAL_TO_BASELINE) {
        const baselineTasks = baselineData?.tasks || [];
        const baselineTasksAndMilestones = baselineTasks.filter(task => task.type === constants.TASK_TYPES.task || task.type === constants.TASK_TYPES.milestone);
        const _baselinePlannedProgress = await this.getPlannedProgress(projectIds[i], baselineTasksAndMilestones);
        const _variance = actualProgress - _baselinePlannedProgress;

        yValues = [
          (variance > 0 ? __('overview_ahead') : __('overview_behind')),
          __('overview_actual_progress'),
          __('overview_baseline_planned_progress'),
        ];
        xValues = [
          {
            value: _variance === 0 ? 0.01 : _variance,
            realValue: _variance,
            label: {
              position: _variance === 0 ? 'right' : 'inside',
              color: _variance === 0 ? '#191919' : '#fff',
            },
            itemStyle: {
              color: _variance < 0 ? '#EE7950' : '#8BC34A',
              borderRadius: _variance < 0 ? [4, 0, 0, 4] : [0, 4, 4, 0],
            },
            progressData: {
              isSingleProjectReport: true,
              projectName: projectData.name,
              name: (_variance > 0 ? __('overview_ahead') : __('overview_behind')),
              isVariance: true,
            },
          },
          {
            value: actualProgress === 0 ? 0.01 : actualProgress,
            realValue: actualProgress,
            label: {
              position: actualProgress === 0 ? 'right' : 'inside',
              color: actualProgress === 0 ? '#191919' : '#fff',
            },
            itemStyle: {
              color: '#1565C0',
              borderRadius: actualProgress < 0 ? [4, 0, 0, 4] : [0, 4, 4, 0],
            },
            progressData: {
              isSingleProjectReport: true,
              projectName: projectData.name,
              name: __('overview_actual_progress'),
              isActual: true,
            },
          },
          {
            value: _baselinePlannedProgress === 0 ? 0.01 : _baselinePlannedProgress,
            realValue: _baselinePlannedProgress,
            label: {
              position: _baselinePlannedProgress === 0 ? 'right' : 'inside',
              color: _baselinePlannedProgress === 0 ? '#191919' : '#fff',
            },
            itemStyle: {
              color: '#458C9A',
              borderRadius: _baselinePlannedProgress < 0 ? [4, 0, 0, 4] : [0, 4, 4, 0],
            },
            progressData: {
              isSingleProjectReport: true,
              projectName: projectData.name,
              name: __('overview_baseline_planned_progress'),
              isPlanned: true,
            },
          },
        ];

        resultConfig.series[0].data = xValues;
        resultConfig.yAxis.axisLabel.fontSize = 16;
        resultConfig.yAxis.data = yValues;

        return { config: resultConfig };
      }

      if (calculationMode === constants.OVERVIEW_TYPES.PLANNED_TO_BASELINE) {
        const baselineTasks = baselineData?.tasks || [];
        const baselineTasksAndMilestones = baselineTasks.filter(task => task.type === constants.TASK_TYPES.task || task.type === constants.TASK_TYPES.milestone);

        const baselineProgress = await this.getPlannedProgress(projectIds[i], baselineTasksAndMilestones);
        const plannedVariance = plannedProgress - baselineProgress;

        yValues = [
          (plannedVariance > 0 ? __('overview_ahead') : __('overview_behind')),
          __('overview_planned_progress'),
          __('overview_baseline_planned_progress'),
        ];
        xValues = [
          {
            value: plannedVariance === 0 ? 0.01 : plannedVariance,
            realValue: plannedVariance,
            label: {
              position: plannedVariance === 0 ? 'right' : 'inside',
              color: plannedVariance === 0 ? '#191919' : '#fff',
            },
            progressData: {
              isSingleProjectReport: true,
              projectName: projectData.name,
              name: (plannedVariance > 0 ? __('overview_ahead') : __('overview_behind')),
              isVariance: true,
            },
          },
          {
            value: plannedProgress === 0 ? 0.01 : plannedProgress,
            realValue: plannedProgress,
            label: {
              position: plannedProgress === 0 ? 'right' : 'inside',
              color: plannedProgress === 0 ? '#191919' : '#fff',
            },
            progressData: {
              isSingleProjectReport: true,
              projectName: projectData.name,
              name: __('overview_planned_progress'),
              isActual: true,
            },
          },
          {
            value: baselineProgress === 0 ? 0.01 : baselineProgress,
            realValue: baselineProgress,
            label: {
              position: baselineProgress === 0 ? 'right' : 'inside',
              color: baselineProgress === 0 ? '#191919' : '#fff',
            },
            progressData: {
              isSingleProjectReport: true,
              projectName: projectData.name,
              name: __('overview_baseline_planned_progress'),
              isPlanned: true,
            },
          },
        ];

        resultConfig.series[0].itemStyle.color = params => {
          if (params.data.progressData.isPlanned) {
            return '#458C9A';
          }
          if (params.data.progressData.isActual) {
            return '#1565C0';
          }

          if (params.data.progressData.isVariance) {
            return (params.value > 0 ? '#8BC34A' : '#EE7950');
          }
        };
        resultConfig.series[0].data = xValues;
        resultConfig.yAxis.axisLabel.fontSize = 16;
        resultConfig.yAxis.data = yValues;

        return { config: resultConfig };
      }

      if (variance !== 0) {
        yValues.push(projectData.name);
        xValues.push({
          value: variance,
          progressData: {
            isSingleProjectReport: false,
            projectName: projectData.name,
            progressValue_1: actualProgress,
            progressValue_2: plannedProgress,
          },
        });
      }
    }

    if (xValues.length) {
      resultConfig.series[0].data = xValues.map(item => ({
        ...item,
        itemStyle: {
          color: item.value < 0 ? '#EE7950' : '#8BC34A',
          borderRadius: item.value < 0 ? [4, 0, 0, 4] : [0, 4, 4, 0],
        },
        label: {
          position: item.value < 0 ? 'inside' : 'insideRight',
        },
      }));
      resultConfig.yAxis.axisLabel.fontSize = 14;
      resultConfig.yAxis.data = yValues;

      return { config: resultConfig };
    }

    return null;
  }
}

const progress = new Progress();

export default progress;
